import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Drawer,
  Form,
  message,
  Radio,
  RadioChangeEvent,
  Space,
  TimePicker,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import {
  getSectionDetail,
  getSquenceDetail,
  getUnitDetailSetting,
  lessonSettingInstructions,
  lessonUnitInstructions,
  sectionsettingInstructions,
} from "../../../service/course-construct";
import { CourseConstructionStore } from "../../../stores/stores";
import { viLocale } from "../../../utils/pickerType";
import "./activeSettingsChaptersDrawer.scss";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

type Props = {
  open?: boolean;
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isSection?: boolean;
  isLesson?: boolean;
  isUnit?: boolean;
  id?: number;
  handleDetail?: any;
  itemDetail?: any;
  itemDetailChapter?: any;
  lessonDetailData?: any;
};

const ActiveSettingsChaptersDrawer = (props: Props) => {
  const { t } = useTranslation();
  const {
    open,
    setIsOpened,
    isSection,
    isLesson,
    id,
    lessonDetailData,
    itemDetail,
    itemDetailChapter,
    handleDetail,
    isUnit,
  } = props;

  const [form] = Form.useForm();
  const { Panel } = Collapse;

  const [availableStatus, setAvailableStatus] = useState<any>(0);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setSelectEndDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [sectionDetail, setSectionDetail] = useState<any>(null);
  const [isSendLearner, setIsSendLearner] = useState<any>(false);
  const [isSendEntireSchool, setIsSendEntireSchool] = useState<any>(false);
  const [isHideSection, setIsHideSection] = useState<any>(false);
  const [scheduleType, setScheduleType] = useState<any>(false);
  const [detailChapter, setDetailChapter] = useState<any>(null);
  const [detailLesson, setDetailLesson] = useState<any>(null);

  const [childRange, setChildRange] = useState<{ start?: string; end?: string } | null>(null);

  const asUtc = (d: any) => (d ? dayjs(d).utc(true) : null);

  const mergeToUtcISO = (d: Dayjs | null, t: Dayjs | null) => {
    if (!d || !t) return "";
    return dayjs(d)
      .utc(true)
      .hour(t.hour())
      .minute(t.minute())
      .second(0)
      .millisecond(0)
      .toISOString();
  };

  const getUtc = (d?: string | null) => (d ? dayjs.utc(d) : null);

  const safeMin = (arr: Array<Dayjs | null>): Dayjs | null => {
    const filtered = arr.filter((d): d is Dayjs => !!d);
    if (!filtered.length) return null;
    return filtered.reduce((a, b) => (a.isBefore(b) ? a : b));
  };
  const safeMax = (arr: Array<Dayjs | null>): Dayjs | null => {
    const filtered = arr.filter((d): d is Dayjs => !!d);
    if (!filtered.length) return null;
    return filtered.reduce((a, b) => (a.isAfter(b) ? a : b));
  };

  const handleRadioChange = (e: RadioChangeEvent) => setAvailableStatus(e.target.value);

  const handleChangeStartDate = (date: any) => {
    const d = asUtc(date);
    setStartDate(d);
    setStartTime(d);
  };
  const handleChangeEndDate = (date: any) => {
    const d = asUtc(date);
    setSelectEndDate(d);
    setEndTime(d);
  };
  const handleChangeStartTime = (time: any | null) => setStartTime(asUtc(time));
  const handleChangeEndTime = (time: any | null) => setEndTime(asUtc(time));

  const getSectionDetailApi = async () => {
    const apiParams = isUnit ? getUnitDetailSetting : (isLesson ? getSquenceDetail : getSectionDetail);
    await apiParams(Number(itemDetail?.id)).then((res) => setSectionDetail(res.data));
  };

  useEffect(() => {
    setAvailableStatus(sectionDetail?.availableStatus ?? 0);

    setStartDate(sectionDetail?.scheduleStartDate ? dayjs.utc(sectionDetail.scheduleStartDate) : null);
    setSelectEndDate(sectionDetail?.scheduleEndDate ? dayjs.utc(sectionDetail.scheduleEndDate) : null);
    setStartTime(sectionDetail?.scheduleStartDate ? dayjs.utc(sectionDetail.scheduleStartDate) : null);
    setEndTime(sectionDetail?.scheduleEndDate ? dayjs.utc(sectionDetail.scheduleEndDate) : null);

    setIsSendLearner(!!sectionDetail?.emailNotificationLearner);
    setIsSendEntireSchool(!!sectionDetail?.emailNotificationAll);
    setIsHideSection(!!sectionDetail?.isHide);
    setScheduleType(sectionDetail?.scheduleType === 1);
  }, [sectionDetail]);

  useEffect(() => { if (isLesson) setDetailChapter(itemDetailChapter); }, [isLesson, itemDetailChapter]);
  useEffect(() => { if (isUnit) setDetailLesson(lessonDetailData); }, [isUnit, lessonDetailData]);
  useEffect(() => { if (open) getSectionDetailApi(); }, [open]);

  const radioItemList = [
    { value: 0, title: t("Bản nháp", "Bản nháp"), describe: t("Mặc định khi khởi tạo. Người học sẽ không thể tìm kiếm hoặc nhìn thấy nội dung này ở trên tầng.", "Mặc định khi khởi tạo. Người học sẽ không thể tìm kiếm hoặc nhìn thấy nội dung này ở trên tầng.") },
    { value: 1, title: t("Riêng tư", "Riêng tư"), describe: t("Nội dung chỉ hiển thị với những Người học được Giảng viên đăng ký hoặc Người học đã trả phí.", "Nội dung chỉ hiển thị với những Người học được Giảng viên đăng ký hoặc Người học đã trả phí.") },
    { value: 2, title: t("Công khai", "Công khai"), describe: t("Nội dung được hiển thị công khai trên danh sách nội dung học và Người học có thể tìm kiếm, học nội dung.", "Nội dung được hiển thị công khai trên danh sách nội dung học và Người học có thể tìm kiếm, học nội dung.") },
  ];
  const onClose = () => { setIsOpened(false); form.resetFields(); };

  const onSaveSetting = () => {
    const formData = new FormData();
    const valueType: any = scheduleType ? 1 : 0;

    const startDateTimeUTC = mergeToUtcISO(startDate, startTime);
    const endDateTimeUTC = mergeToUtcISO(endDate, endTime);

    formData.append("id", itemDetail?.id ?? null);
    formData.append("availableStatus", availableStatus ?? 0);
    formData.append("scheduleType", valueType ?? false);
    formData.append("scheduleStartDate", startDateTimeUTC);
    formData.append("scheduleEndDate", endDateTimeUTC);
    formData.append("emailNotificationAll", isSendEntireSchool ?? false);
    formData.append("emailNotificationLearner", isSendLearner ?? false);
    formData.append("isHide", isHideSection ?? false);

    const submitApiUrl = isUnit
      ? lessonUnitInstructions(formData)
      : isLesson
        ? lessonSettingInstructions(formData)
        : sectionsettingInstructions(formData);

    submitApiUrl
      .then(() => {
        handleDetail();
        message.success("Cài đặt hoạt động " + `${itemDetail?.name}` + " thành công");
        onClose()
      })
      .catch((e) => console.error("Error saving settings:", e));
  };


  const { sectionsState, chapterDetail, lessonDetail, setChapterDetail, setLessonDetail } =
    CourseConstructionStore();

  useEffect(() => {
    if (!open || !itemDetail?.id || !sectionsState.length) return;

    setChapterDetail(null);
    setLessonDetail(null);

    sectionsState.forEach((chapter: any) => {
      if (isLesson) {
        const lesson = chapter.children?.find((l: any) => l.id === itemDetail.id);
        if (lesson) {
          setChapterDetail({
            scheduleStartDate: chapter.scheduleStartDate || chapter.schuduleStartDate,
            scheduleEndDate: chapter.scheduleEndDate || chapter.schuduleEndDate,
          });
        }
      }

      if (isUnit) {
        chapter.children?.forEach((lesson: any) => {
          const unit = lesson.children?.find((u: any) => u.id === itemDetail.id);
          if (unit) {
            setChapterDetail({
              scheduleStartDate: chapter.scheduleStartDate || chapter.schuduleStartDate,
              scheduleEndDate: chapter.scheduleEndDate || chapter.schuduleEndDate,
            });
            if (lesson.scheduleStartDate || lesson.scheduleEndDate) {
              setLessonDetail({
                scheduleStartDate: lesson.scheduleStartDate,
                scheduleEndDate: lesson.scheduleEndDate,
              });
            }
          }
        });
      }
    });
  }, [open, itemDetail, sectionsState, isLesson, isUnit, setChapterDetail, setLessonDetail]);

  const parentDates = {
    start: isUnit && lessonDetail?.scheduleStartDate ? lessonDetail.scheduleStartDate : chapterDetail?.scheduleStartDate,
    end: isUnit && lessonDetail?.scheduleEndDate ? lessonDetail.scheduleEndDate : chapterDetail?.scheduleEndDate,
  };

  type Range = { start?: string; end?: string } | null;

  const extractRangeFromNodes = (nodes: any[]): Range => {
    const starts: Array<Dayjs | null> = [];
    const ends: Array<Dayjs | null> = [];
    nodes?.forEach((n: any) => {
      const s = getUtc(n.scheduleStartDate || n.schuduleStartDate);
      const e = getUtc(n.scheduleEndDate || n.schuduleEndDate);
      if (s && e) { starts.push(s); ends.push(e); }
    });
    const minS = safeMin(starts);
    const maxE = safeMax(ends);
    if (!minS || !maxE) return null;
    return { start: minS.toISOString(), end: maxE.toISOString() };
  };

  useEffect(() => {
    if (!open || !itemDetail?.id || !sectionsState?.length) return;
    setChildRange(null);

    if (isSection) {
      const chapter = sectionsState.find((c: any) => c.id === itemDetail.id);
      if (!chapter) return;

      const lessonNodes = chapter.children || [];
      const lessonRange = extractRangeFromNodes(lessonNodes);
      if (lessonRange) { setChildRange(lessonRange); return; }

      const unitNodes = lessonNodes.flatMap((l: any) => l.children || []);
      const unitRange = extractRangeFromNodes(unitNodes);
      setChildRange(unitRange || null);
    }

    if (isLesson) {
      sectionsState.forEach((ch: any) => {
        const l = ch.children?.find((x: any) => x.id === itemDetail.id);
        if (!l) return;
        const unitRange = extractRangeFromNodes(l.children || []);
        if (unitRange) setChildRange(unitRange);
      });
    }
  }, [open, itemDetail, sectionsState, isSection, isLesson]);

  const disabledDateByParent = (current: any) => {
    if (!parentDates.start || !parentDates.end) return false;
    const startUTC = dayjs.utc(parentDates.start);
    const endUTC = dayjs.utc(parentDates.end);
    const currentUTC = dayjs(current).utc(true);
    return currentUTC.isBefore(startUTC.startOf("day")) || currentUTC.isAfter(endUTC.endOf("day"));
  };

  const disabledDateStart = (current: any) => {
    let disabled = false;
    if (isLesson || isUnit) disabled = disabledDateByParent(current);

    if (childRange?.start) {
      const minChildStart = dayjs.utc(childRange.start);
      const currentUTC = dayjs(current).utc(true);
      if (currentUTC.isAfter(minChildStart, "day")) disabled = true;
    }
    return disabled;
  };

  const disabledDateEnd = (current: any) => {
    let disabled = false;
    if (isLesson || isUnit) disabled = disabledDateByParent(current);

    if (childRange?.end) {
      const maxChildEnd = dayjs.utc(childRange.end);
      const currentUTC = dayjs(current).utc(true);
      if (currentUTC.isBefore(maxChildEnd, "day")) disabled = true;
    }
    return disabled;
  };

  const getDisabledTime = (type: "start" | "end", selectedDate: any) => {
    if (!selectedDate || !parentDates.start || !parentDates.end) return {};

    const selected = dayjs(selectedDate).utc(true);
    const parentStartUTC = dayjs.utc(parentDates.start);
    const parentEndUTC = dayjs.utc(parentDates.end);

    if (type === "start" && selected.isSame(parentStartUTC, "day")) {
      const h = parentStartUTC.hour();
      const m = parentStartUTC.minute();
      return {
        disabledHours: () => Array.from({ length: h }, (_, i) => i),
        disabledMinutes: (hour: number) => (hour === h ? Array.from({ length: m }, (_, i) => i) : []),
      };
    }

    if (type === "end" && selected.isSame(parentEndUTC, "day")) {
      const h = parentEndUTC.hour();
      const m = parentEndUTC.minute();
      return {
        disabledHours: () => Array.from({ length: 23 - h }, (_, i) => h + i + 1),
        disabledMinutes: (hour: number) => (hour === h ? Array.from({ length: 59 - m }, (_, i) => m + i + 1) : []),
      };
    }

    if (type === "start" && endDate && selected.isSame(dayjs(endDate).utc(true), "day")) {
      const h = endTime ? endTime.hour() : 23;
      const m = endTime ? endTime.minute() : 59;
      return {
        disabledHours: () => Array.from({ length: 23 - h }, (_, i) => h + i + 1),
        disabledMinutes: (hour: number) => (hour === h ? Array.from({ length: 59 - m }, (_, i) => m + i + 1) : []),
      };
    }

    if (type === "end" && startDate && selected.isSame(dayjs(startDate).utc(true), "day")) {
      const h = startTime ? startTime.hour() : 0;
      const m = startTime ? startTime.minute() : 0;
      return {
        disabledHours: () => Array.from({ length: h + 1 }, (_, i) => i),
        disabledMinutes: (hour: number) => (hour === h ? Array.from({ length: m + 1 }, (_, i) => i) : []),
      };
    }

    return {};
  };

  const unionNums = (a: number[] = [], b: number[] = []) =>
    Array.from(new Set([...a, ...b])).sort((x, y) => x - y);

  const getDisabledTimeWithChildren = (type: "start" | "end", selectedDate: any) => {
    const base: any = getDisabledTime(type, selectedDate);
    const baseDisabledHours: number[] = base?.disabledHours ? base.disabledHours() : [];
    const baseDisabledMinutes = base?.disabledMinutes || ((_: number) => [] as number[]);

    if (!selectedDate || !childRange) {
      return {
        disabledHours: () => baseDisabledHours,
        disabledMinutes: (h: number) => baseDisabledMinutes(h),
      };
    }

    const selected = dayjs(selectedDate).utc(true);

    if (type === "start" && childRange.start) {
      const minS = dayjs.utc(childRange.start);
      if (selected.isSame(minS, "day")) {
        const h = minS.hour();
        const m = minS.minute();
        const childHours = Array.from({ length: 23 - h }, (_, i) => h + i + 1);
        const childMinutes = (hour: number) => (hour === h ? Array.from({ length: 59 - m }, (_, i) => m + i + 1) : []);
        return {
          disabledHours: () => unionNums(baseDisabledHours, childHours),
          disabledMinutes: (hour: number) => unionNums(baseDisabledMinutes(hour), childMinutes(hour)),
        };
      }
    }

    if (type === "end" && childRange.end) {
      const maxE = dayjs.utc(childRange.end);
      if (selected.isSame(maxE, "day")) {
        const h = maxE.hour();
        const m = maxE.minute();
        const childHours = Array.from({ length: h }, (_, i) => i);
        const childMinutes = (hour: number) => (hour === h ? Array.from({ length: m }, (_, i) => i) : []);
        return {
          disabledHours: () => unionNums(baseDisabledHours, childHours),
          disabledMinutes: (hour: number) => unionNums(baseDisabledMinutes(hour), childMinutes(hour)),
        };
      }
    }

    return {
      disabledHours: () => baseDisabledHours,
      disabledMinutes: (h: number) => baseDisabledMinutes(h),
    };
  };

  return (
    <Drawer
      title={"Cài đặt hoạt động " + `${itemDetail?.name}`}
      open={open}
      onClose={onClose}
      width={736}
      bodyStyle={{ padding: "0px" }}
    >
      <Form className="active-settings-chapter" layout={"vertical"} form={form} name="active-settings-chapter">
        <div>
          <Collapse defaultActiveKey={["1", "2", "3"]}>
            <Panel header={t("Truy cập", "Truy cập")} key="1" className="setting-chapter-title">
              <Radio.Group value={availableStatus}>
                {radioItemList.map((item, index) => (
                  <div key={index} className="radio-item wrapper-radio">
                    <Radio value={item.value} onChange={handleRadioChange}>
                      <Typography.Title className="setting-chapter-title" level={5}>{item.title}</Typography.Title>
                    </Radio>
                    <div className="setting-chapter-description">{item.describe}</div>
                  </div>
                ))}
              </Radio.Group>
            </Panel>

            <Panel header={t("Lịch trình", "Lịch trình")} key="2">
              {isLesson && (
                <div className="radio-item">
                  <Checkbox checked={scheduleType} onChange={(e) => setScheduleType(e.target.checked)} />
                  <span className="setting-chapter-description">{t("Theo khoá học", "Theo khoá học")}</span>
                </div>
              )}
              {isUnit && (
                <div className="radio-item">
                  <Checkbox checked={scheduleType} onChange={(e) => setScheduleType(e.target.checked)} />
                  <span className="setting-chapter-description">{t("Theo bài giảng", "Theo bài giảng")}</span>
                </div>
              )}

              {!scheduleType ? (
                <>
                  {/* START */}
                  <div className="time-picker">
                    <div className="time-picker-content">
                      <span>{t("Ngày bắt đầu", "Ngày bắt đầu")}</span>
                      <DatePicker
                        onChange={handleChangeStartDate}
                        locale={viLocale}
                        format={"DD/MM/YYYY"}
                        value={startDate}
                        disabledDate={
                          isSection || isLesson
                            ? disabledDateStart
                            : isUnit
                              ? disabledDateByParent
                              : undefined
                        }
                        maxDate={endDate ? endDate : undefined}
                      />
                    </div>
                    <div className="time-picker-content">
                      <span>{t("Giờ", "Giờ")}</span>
                      <TimePicker
                        locale={viLocale}
                        value={startTime}
                        onChange={handleChangeStartTime}
                        format={"HH:mm"}
                        disabledTime={() =>
                          isSection || isLesson
                            ? getDisabledTimeWithChildren("start", startDate)
                            : getDisabledTime("start", startDate)
                        }
                      />
                    </div>
                  </div>

                  <div className="time-picker">
                    <div className="time-picker-content">
                      <span>{t("Ngày kết thúc", "Ngày kết thúc")}</span>
                      <DatePicker
                        onChange={handleChangeEndDate}
                        locale={viLocale}
                        format={"DD/MM/YYYY"}
                        value={endDate}
                        disabledDate={
                          isSection || isLesson
                            ? disabledDateEnd
                            : isUnit
                              ? disabledDateByParent
                              : undefined
                        }
                        minDate={startDate ? startDate : undefined}
                      />
                    </div>
                    <div className="time-picker-content">
                      <span>{t("Giờ", "Giờ")}</span>
                      <TimePicker
                        locale={viLocale}
                        value={endTime}
                        onChange={handleChangeEndTime}
                        format={"HH:mm"}
                        disabledTime={() =>
                          isSection || isLesson
                            ? getDisabledTimeWithChildren("end", endDate)
                            : getDisabledTime("end", endDate)
                        }
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </Panel>

            <Panel header={t("Email thông báo", "Email thông báo")} key="3">
              <div className="radio-item">{t("Xác định đối tượng nhận thông báo ngay khi chương mới được cập nhập", "Xác định đối tượng nhận thông báo ngay khi chương mới được cập nhập")}</div>
              <div className="radio-item">
                <Checkbox checked={isSendEntireSchool} onChange={(e) => setIsSendEntireSchool(e.target.checked)} />
                <span className="setting-chapter-description">{t("Gửi email thông báo tới toàn trường", "Gửi email thông báo tới toàn trường")}</span>
              </div>
              <div className="radio-item">
                <Checkbox checked={isSendLearner} onChange={(e) => setIsSendLearner(e.target.checked)} />
                <span className="setting-chapter-description">{t("Gửi email thông báo tới người học", "Gửi email thông báo tới người học")}</span>
              </div>
            </Panel>
          </Collapse>

          <div className="radio-item">
            <Checkbox checked={isHideSection} onChange={(e) => setIsHideSection(e.target.checked)} />
            <span className="setting-chapter-description">
              {isLesson ? t("Ẩn bài giảng", "Ẩn bài giảng") : isUnit ? t("Ẩn học liệu", "Ẩn học liệu") : t("Ẩn chương", "Ẩn chương")}
            </span>
            <div className="radio-item">
              {t("Học viên không thể nhìn thấy nội dung nào trong", "Học viên không thể nhìn thấy nội dung nào trong")} {isUnit ? t("học liệu", "học liệu") : t("chương", "chương")} {t("này", "này")}
            </div>
          </div>
        </div>

        <Space className="btn-save-setting pb-5">
          <Button className="btn btn-primary" onClick={onSaveSetting}>
            <Typography.Text>{t("Lưu", "Lưu")}</Typography.Text>
          </Button>
          <Button onClick={onClose} className="btn btn-outlined">
            <Typography.Text>{t("Đóng", "Đóng")}</Typography.Text>
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default ActiveSettingsChaptersDrawer;
